import React from 'react';
import { IconCross } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { Badge } from './Badge';

export type IconButtonSize = 'default' | 'small' | 'medium';
export type IconButtonState = 'default' | 'hover' | 'press' | 'disabled';
export type IconButtonType =
  | 'tertiary'
  | 'primary'
  | 'active'
  | 'activeWithBadge'
  | 'transparent'
  | 'inverted';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Size of the button */
  size?: IconButtonSize;
  /** Visual state - typically controlled by hover/press automatically */
  state?: IconButtonState;
  /** Type/variant of the button */
  type?: IconButtonType;
  /** Icon to display (defaults to IconCross) */
  icon?: React.ComponentType<{ className?: string; width?: number; height?: number }>;
  /** Badge count (only shown for activeWithBadge type) */
  badgeCount?: number;
  /** Additional CSS classes */
  className?: string;
  /** Override the icon color/classes (e.g. an accent icon on a light background). */
  iconClassName?: string;
}

export function IconButton({
  size = 'default',
  state,
  type = 'transparent',
  icon: Icon = IconCross,
  badgeCount,
  className,
  iconClassName,
  disabled,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  ...props
}: IconButtonProps) {
  const [internalState, setInternalState] = React.useState<'default' | 'hover' | 'press'>(
    'default',
  );
  const isDisabled = disabled || state === 'disabled';
  const currentState = state || (isDisabled ? 'disabled' : internalState);

  // Handle internal state management if state prop is not provided
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && !state) {
      setInternalState('hover');
    }
    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && !state) {
      setInternalState('default');
    }
    onMouseLeave?.(e);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && !state) {
      setInternalState('press');
    }
    onMouseDown?.(e);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && !state) {
      setInternalState('hover');
    }
    onMouseUp?.(e);
  };

  // Size configurations
  const sizeConfig = {
    default: {
      button: 'w-10 h-10', // 40px
      icon: 16,
    },
    medium: {
      button: 'w-9 h-9', // 36px
      icon: 16,
    },
    small: {
      button: 'w-8 h-8', // 32px
      icon: 16,
    },
  };

  const config = sizeConfig[size];

  // Get styles based on type and state
  const getButtonStyles = () => {
    const baseStyles =
      'relative inline-flex items-center justify-center rounded-full transition-all';
    const focusStyles =
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent';

    if (type === 'tertiary') {
      return cn(
        baseStyles,
        focusStyles,
        'bg-transparent',
        currentState === 'hover' && 'shadow-md',
        currentState === 'press' && 'shadow-lg',
        currentState === 'disabled' && 'opacity-40 cursor-not-allowed',
        !isDisabled && 'cursor-pointer',
      );
    }

    if (type === 'transparent') {
      return cn(
        baseStyles,
        focusStyles,
        config.button,
        'bg-trans shadow',
        currentState === 'hover' && 'bg-transparent-hover',
        currentState === 'press' && 'bg-transparent-hover',
        currentState === 'disabled' && 'opacity-60 cursor-not-allowed',
        !isDisabled && 'cursor-pointer',
      );
    }

    // Primary type - accent background
    if (type === 'primary') {
      return cn(
        baseStyles,
        focusStyles,
        config.button,
        'bg-accent',
        currentState === 'hover' && 'bg-accent-hover',
        currentState === 'press' && 'bg-accent-hover',
        currentState === 'disabled' && 'opacity-60 cursor-not-allowed bg-tertiary',
        !isDisabled && 'cursor-pointer',
      );
    }

    // Active type - active background
    if (type === 'active' || type === 'activeWithBadge') {
      return cn(
        baseStyles,
        focusStyles,
        config.button,
        'bg-active',
        currentState === 'hover' && 'bg-active-hover',
        currentState === 'press' && 'bg-active-hover',
        currentState === 'disabled' && 'opacity-60 cursor-not-allowed',
        !isDisabled && 'cursor-pointer',
      );
    }

    // Inverted type - inverted background
    if (type === 'inverted') {
      return cn(
        baseStyles,
        focusStyles,
        config.button,
        'bg-inverted',
        currentState === 'hover' && 'bg-inverted-hover',
        currentState === 'press' && 'bg-inverted-hover',
        currentState === 'disabled' && 'opacity-60 cursor-not-allowed',
        !isDisabled && 'cursor-pointer',
      );
    }

    return baseStyles;
  };

  // Get icon color based on type and state
  const getIconColor = () => {
    if (currentState === 'disabled') {
      return 'icon-tertiary';
    }

    // Transparent type - dark gray icon
    if (type === 'transparent') {
      return 'icon-primary';
    }

    // Tertiary type - secondary color icon
    if (type === 'tertiary') {
      return 'icon-secondary';
    }

    // Primary, Active, ActiveWithBadge, Inverted - white icon on colored background
    if (
      type === 'primary' ||
      type === 'active' ||
      type === 'activeWithBadge' ||
      type === 'inverted'
    ) {
      return 'icon-inverted';
    }

    return 'icon-secondary';
  };

  const showBadge = type === 'activeWithBadge' && badgeCount !== undefined && badgeCount > 0;
  const badgePosition = size === 'small' ? 'right-[-14px]' : 'right-[-10px]';

  return (
    <button
      className={cn(getButtonStyles(), className)}
      disabled={isDisabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...props}
    >
      <Icon
        className={cn('shrink-0', getIconColor(), iconClassName)}
        width={config.icon}
        height={config.icon}
      />
      {showBadge && (
        <div className={cn('absolute top-[-6px]', badgePosition)}>
          <Badge type="accent" count={badgeCount} border />
        </div>
      )}
    </button>
  );
}

export default IconButton;
